'use client';

import { cn } from '@/lib/utils';
import Markdown from 'markdown-to-jsx';
import useSWR from 'swr';

interface KnowledgeBaseViewProps {
  fileName: string;
}

export default function KnowledgeBaseView({ fileName }: KnowledgeBaseViewProps) {
  const { data: res } = useSWR(fileName ? `research:/knowledge-base/${fileName}/content` : null);

  if (res?.success && res?.data?.content) {
    return (
      <div
        className={cn(
          'markdown-wrapper',
          // 表头样式
          '[&_thead]:bg-gray-50 dark:[&_thead]:bg-[#1a1a1a]',
          '[&_th]:px-4 [&_th]:py-3 [&_th]:text-center [&_th]:text-xs [&_th]:font-medium',
          '[&_th]:tracking-wider [&_th]:text-text-primary dark:[&_th]:text-text-tertiary',
          '[&_th]:border-b [&_th]:border-gray-200 dark:[&_th]:border-[#222222]',
          '[&_th]:whitespace-nowrap',
          // 表体样式
          '[&_tbody_tr]:border-b [&_tbody_tr]:border-gray-200 dark:[&_tbody_tr]:border-[#171717]',
          '[&_tbody_tr:hover]:bg-gray-50 dark:[&_tbody_tr:hover]:bg-gray-800',
          '[&_tbody_tr:last-child]:border-b-0',
          // 单元格样式
          '[&_td]:px-4 [&_td]:py-3 [&_td]:text-[12px] [&_td]:text-center',
          '[&_td]:text-text-secondary dark:[&_td]:text-text-tertiary [&_td]:whitespace-nowrap',
        )}
      >
        <Markdown
          className={cn(
            'max-w-none break-words font-light text-text-primary',
            '[&_h1]:my-[30px] [&_h1]:py-0 [&_h1]:text-[34px] [&_h1]:font-normal [&_h1]:leading-tight [&_h1]:text-text-primary',
            '[&_h2]:my-5 [&_h2]:py-0 [&_h2]:text-[24px] [&_h2]:font-normal [&_h2]:leading-tight [&_h2]:text-text-primary',
            '[&_h3]:my-5 [&_h3]:py-0 [&_h3]:text-[18px] [&_h3]:font-[600] [&_h3]:leading-snug [&_h3]:text-text-primary',
            '[&_h4]:mb-3 [&_h4]:mt-4 [&_h4]:text-base [&_h4]:font-semibold [&_h4]:text-text-primary',
            '[&_p]:mb-3 [&_p]:leading-7',
            '[&_strong]:font-semibold [&_strong]:text-text-primary',
            '[&_a]:text-brand [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-brand-hover',
            '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6',
            '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6',
            '[&_li]:my-1.5 [&_li]:leading-7',
            '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-border-card [&_blockquote]:pl-4 [&_blockquote]:text-text-secondary',
            '[&_code]:rounded [&_code]:bg-bg-card [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm [&_code]:text-text-primary',
            '[&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-bg-card [&_pre]:p-0',
            '[&_pre_code]:block [&_pre_code]:bg-transparent [&_pre_code]:p-4 [&_pre_code]:text-sm',
            '[&_table]:my-0 [&_table]:w-full [&_table]:text-sm',
            '[&_img]:my-4 [&_img]:max-w-full [&_img]:rounded-lg',
            '[&_hr]:my-6 [&_hr]:border-border-card',
          )}
        >
          {res.data.content}
        </Markdown>
      </div>
    );
  }

  return null;
}
